import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  writeBatch,
  deleteDoc,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { getInitials } from "./utils";
import SwipeWrapper from './SwipeWrapper';
import { sendFollowNotification } from './notificationService';
import { isNative } from "./utils/isNative";
import { FaHeart, FaUserPlus, FaUsers, FaTrash } from 'react-icons/fa';
import { Capacitor } from '@capacitor/core';

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [following, setFollowing] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Debug: stampiamo i valori per vedere cosa sta succedendo
  console.log("NotificationsPage - location.state:", location.state);
  console.log("NotificationsPage - previousPage will be:", location.state?.from || "/");

  // Se non abbiamo uno stato di navigazione, torniamo alla home
  const previousPage = location.state?.from || "/";

  // ℹ️ NOTA: Il badge iOS e il Notification Center sono gestiti automaticamente
  // dalla Navbar.jsx tramite il listener su unreadCount.
  // Quando marchiamo le notifiche come isRead: true qui sotto, Navbar rileva
  // che unreadCount = 0 e automaticamente pulisce tutto (badge + Notification Center).

  useEffect(() => {
    const resetNotificationsBadge = async () => {
      if (!currentUserId) return;
      
      const q = query(
        collection(db, "notifications"),
        where("recipientId", "==", currentUserId),
        where("isRead", "==", false)
      );
      const snap = await getDocs(q);
      
      if (snap.empty) {
        console.log('✅ [NotificationsPage] Nessuna notifica non letta da marcare');
        return;
      }
      
      const batch = writeBatch(db);
      snap.docs.forEach(docSnap => {
        batch.update(doc(db, "notifications", docSnap.id), { isRead: true });
      });
      await batch.commit();
      console.log(`✅ [NotificationsPage] ${snap.size} notifiche marcate come lette`);
      console.log('🔔 [NotificationsPage] Badge iOS si aggiornerà automaticamente via Navbar listener');
    };
    resetNotificationsBadge();
  }, [currentUserId]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUserId(user.uid);
        const userRef = doc(db, "users", user.uid);
        getDoc(userRef).then((snap) => {
          if (snap.exists()) {
            setFollowing(snap.data().following || []);
          }
        });
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUserId) return;

    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", currentUserId),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, async (snapshot) => {
      const nots = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          
          // Skip notifications without senderId
          if (!data.senderId) {
            console.warn("⚠️ [NotificationsPage] Notifica senza senderId, skip:", docSnap.id);
            return null;
          }
          
          const senderSnap = await getDoc(doc(db, "users", data.senderId));
          const sender = senderSnap.exists() ? senderSnap.data() : {};
          const initials = getInitials(sender.displayName || "Utente");
          return {
            id: docSnap.id,
            ...data,
            senderName: sender.displayName || "Utente",
            senderInitials: initials,
            senderAvatar: sender.avatar || "",
          };
        })
      );
      setNotifications(nots.filter(n => n !== null));

      // ✅ NO DOM manipulation - React handles badge visibility automatically
      // Badge disappears when notifications are marked as read (isRead: true)
      // and Firestore onSnapshot in Navbar.jsx updates unreadCount to 0
    });

    return () => unsub();
  }, [currentUserId]);

  const deleteNotification = async (notificationId) => {
    try {
      await deleteDoc(doc(db, "notifications", notificationId));
      console.log(`✅ Notifica ${notificationId} eliminata`);
    } catch (error) {
      console.error("❌ Errore eliminazione notifica:", error);
    }
  };

  const toggleFollow = async (targetUserId) => {
    console.log("🔵 [NotificationsPage][toggleFollow] INIZIO - targetUserId:", targetUserId, "platform:", Capacitor.getPlatform());
    
    // ✅ FIX: Usa auth.currentUser.uid direttamente invece di currentUserId dallo state
    if (!auth.currentUser) {
      console.log("❌ [NotificationsPage][toggleFollow] Utente non autenticato per follow");
      return;
    }

    const authenticatedUserId = auth.currentUser.uid;
    console.log("✅ [NotificationsPage][toggleFollow] Auth OK, user:", authenticatedUserId);

    if (!targetUserId) {
      console.warn("⚠️ [NotificationsPage][toggleFollow] targetUserId mancante");
      return;
    }

    const isFollowing = following.includes(targetUserId);
    console.log("✅ [NotificationsPage][toggleFollow] Check OK, user:", authenticatedUserId, "target:", targetUserId, "isFollowing:", isFollowing);

    const userRef = doc(db, "users", authenticatedUserId);
    const targetRef = doc(db, "users", targetUserId);

    console.log("🔵 [NotificationsPage][toggleFollow] Aggiorno Firestore...");
    await updateDoc(userRef, {
      following: isFollowing ? arrayRemove(targetUserId) : arrayUnion(targetUserId),
    });
    await updateDoc(targetRef, {
      followers: isFollowing ? arrayRemove(authenticatedUserId) : arrayUnion(authenticatedUserId),
    });
    console.log("✅ [NotificationsPage][toggleFollow] Firestore aggiornato");

    // Invia notifica solo quando si fa follow (non unfollow)
    if (!isFollowing) {
      console.log("🔵 [NotificationsPage][toggleFollow] Era unfollow, devo inviare notifica...");
      try {
        console.log("🔵 [NotificationsPage][toggleFollow] Fetching sender name...");
        // Ottieni il nome del sender dal documento currentUser
        const currentUserDoc = await getDoc(doc(db, "users", authenticatedUserId));
        console.log("✅ [NotificationsPage][toggleFollow] getDoc completato, exists:", currentUserDoc.exists());
        
        const senderName = currentUserDoc.exists() ? currentUserDoc.data().displayName || t('common.user') : t('common.user');
        console.log("✅ [NotificationsPage][toggleFollow] senderName:", senderName);
        
        console.log('🔵 [Notifications][NotificationsPage] CHIAMO sendFollowNotification da piattaforma:', Capacitor.getPlatform(), 'sender:', authenticatedUserId, 'recipient:', targetUserId);
        await sendFollowNotification(authenticatedUserId, senderName, targetUserId);
        console.log("✅✅✅ [NotificationsPage][toggleFollow] Notifica follow inviata con successo da piattaforma:", Capacitor.getPlatform());
      } catch (notifError) {
        console.error("❌❌❌ [NotificationsPage][toggleFollow] ERRORE notifica follow:", notifError);
        console.error("❌ [NotificationsPage][toggleFollow] Error message:", notifError.message);
        console.error("❌ [NotificationsPage][toggleFollow] Error code:", notifError.code);
        console.error("❌ [NotificationsPage][toggleFollow] Stack trace:", notifError.stack);
      }
    } else {
      console.log("ℹ️ [NotificationsPage][toggleFollow] Era già following, skip notifica (unfollow)");
    }

    console.log("🔵 [NotificationsPage][toggleFollow] Fetching updated following array...");
    const snap = await getDoc(userRef);
    setFollowing(snap.data().following || []);
    console.log("✅ [NotificationsPage][toggleFollow] Following state aggiornato");
    
    console.log("🔵 [NotificationsPage][toggleFollow] FINE");
  };

  const groupByDate = (notifs) => {
    const groups = {};
    const now = new Date();
    const today = now.toDateString();
    
    // Calculate "this week" threshold (7 days ago)
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(now.getDate() - 7);

    for (let notif of notifs) {
      const notifDate = notif.createdAt?.toDate?.() || new Date();
      const notifDateStr = notifDate.toDateString();
      
      let key;
      if (notifDateStr === today) {
        key = t('notifications.today');
      } else if (notifDate >= thisWeekStart) {
        key = t('notifications.thisWeek');
      } else {
        key = t('notifications.older');
      }
      
      if (!groups[key]) groups[key] = [];
      groups[key].push(notif);
    }
    return groups;
  };

  const grouped = groupByDate(notifications);

  console.log("NotificationsPage - Rendering with previousPage:", previousPage);

  return (
    <SwipeWrapper redirectTo={previousPage}>
      <div style={{ fontFamily: "Poppins, sans-serif", padding: "20px", paddingTop: "35px", paddingBottom: "80px" }}>
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ margin: 0, marginTop: "12px" }}>{t('notifications.title')}</h2>
      </div>

      {Object.keys(grouped).map((date) => (
        <div key={date}>
          <h4 style={{ marginTop: "20px", color: "#555" }}>{date}</h4>
          {grouped[date].map((n) => {
            const isUnread = n.isRead === false;
            
            const getNotificationIcon = (type) => {
              switch(type) {
                case 'like':
                  return <FaHeart style={{ color: '#ff4458', fontSize: '14px' }} />;
                case 'follow':
                  return <FaUserPlus style={{ color: '#4CAF50', fontSize: '14px' }} />;
                case 'collaboration':
                  return <FaUsers style={{ color: '#2196F3', fontSize: '14px' }} />;
                default:
                  return null;
              }
            };

            return (
              <div
                key={n.id}
                style={{
                  background: isUnread ? "#f0f7ff" : "#f9f9f9",
                  borderRadius: "10px",
                  padding: "12px",
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  borderLeft: isUnread ? "3px solid #2196F3" : "3px solid transparent",
                }}
              >
                <div style={{ position: "relative", marginRight: "12px" }}>
                  {n.senderAvatar ? (
                    <img 
                      src={n.senderAvatar} 
                      alt="Avatar" 
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "50%",
                        backgroundColor: "#000",
                        color: "#fff",
                        fontWeight: "bold",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "14px",
                        flexShrink: 0,
                      }}
                    >
                      {n.senderInitials}
                    </div>
                  )}
                  <div style={{
                    position: "absolute",
                    bottom: "-2px",
                    right: "-2px",
                    background: "#fff",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}>
                    {getNotificationIcon(n.type)}
                  </div>
                </div>
                
                {isUnread && (
                  <div style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#2196F3",
                    marginRight: "8px",
                    flexShrink: 0,
                  }} />
                )}

                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: isUnread ? "600" : "normal" }}>
                    <strong
                      onClick={() => navigate(`/utente/${n.senderId}`, { state: { from: "/notifications" } })}
                      style={{ cursor: "pointer" }}
                    >
                      {n.senderName}
                    </strong>{" "}
                    {n.type === "like" ? (
                      <>
                        {t('notifications.like')}{" "}
                        <span
                          style={{ textDecoration: "underline", cursor: "pointer" }}
                          onClick={() => navigate(`/wishlist/${n.wishlistId}`)}
                        >
                          {t('navigation.wishlist')}
                        </span>.
                      </>
                    ) : n.type === "collaboration" ? (
                      <>
                        {t('notifications.collaboration')}{" "}
                        <span
                          style={{ textDecoration: "underline", cursor: "pointer" }}
                          onClick={() => navigate(`/wishlist/${n.wishlistId}`)}
                        >
                          {t('navigation.wishlist')}
                        </span>.
                      </>
                    ) : (
                      t('notifications.follow')
                    )}
                  </span>
                </div>
                
                {n.type === "follow" && (
                  <button
                    style={{
                      marginLeft: "10px",
                      background: "#000",
                      color: "#fff",
                      border: "none",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      fontSize: "13px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => toggleFollow(n.senderId)}
                  >
                    {following.includes(n.senderId) ? t('feed.unfollow') : t('feed.follow')}
                  </button>
                )}
                {n.type === "collaboration" && (
                  <button
                    style={{
                      marginLeft: "10px",
                      background: "#000",
                      color: "#fff",
                      border: "none",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      fontSize: "13px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => navigate(`/wishlist/${n.wishlistId}`)}
                  >
                    {t('common.view')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
      </div>
    </SwipeWrapper>
  );
}